import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { environment } from 'src/environments/environment';
import {
  HiddenEmployeeTarget,
  HiddenMigrationResult,
  HiddenToEmployeeControlledService,
} from 'src/app/Services/hidden-to-employee-controlled.service';
import { ProjectService } from 'src/app/Services/project.service';

export interface HiddenToGeneralDialogData {
  employees: HiddenEmployeeTarget[];
}

type DialogStep = 'confirm' | 'processing' | 'result';

@Component({
  selector: 'app-hidden-to-general-dialog',
  templateUrl: './hidden-to-general-dialog.component.html',
  styleUrls: ['./hidden-to-general-dialog.component.css'],
  standalone: false,
})
export class HiddenToGeneralDialogComponent {
  step: DialogStep = 'confirm';
  results: HiddenMigrationResult[] = [];
  errorMessage = '';

  readonly generalDownloads = [
    { label: 'Windows 10 & 11', action: () => this.projectService.downloadRustGeneralAgent() },
    { label: 'Windows 7 & 8', action: () => this.projectService.downloadGeneralAgent('Windows 7 & 8') },
    { label: 'Mac', action: () => this.projectService.downloadGeneralAgent('Mac') },
    { label: 'Linux', action: () => this.projectService.downloadGeneralAgent('Linux') },
  ];

  readonly macInstallerUrl = environment.app.macInstallerUrl;
  readonly linuxInstallerUrl = environment.app.linuxInstallerUrl;

  constructor(
    public dialogRef: MatDialogRef<HiddenToGeneralDialogComponent, boolean>,
    @Inject(MAT_DIALOG_DATA) public data: HiddenToGeneralDialogData,
    private migrationService: HiddenToEmployeeControlledService,
    private projectService: ProjectService
  ) {}

  get employeeCount(): number {
    return this.data.employees.length;
  }

  get successfulResults(): HiddenMigrationResult[] {
    return this.results.filter((result) => result.success);
  }

  get failedResults(): HiddenMigrationResult[] {
    return this.results.filter((result) => !result.success);
  }

  getEmployeeInitials(name: string): string {
    const trimmed = String(name || '').trim();
    if (!trimmed) {
      return '?';
    }
    const parts = trimmed.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return trimmed.slice(0, 2).toUpperCase();
  }

  getEmployeeTooltip(employee: HiddenEmployeeTarget): string {
    return employee.email?.trim() || employee.name;
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }

  onConfirm(): void {
    this.step = 'processing';
    this.errorMessage = '';

    this.migrationService.migrateEmployees(this.data.employees).subscribe({
      next: (results) => {
        this.results = results;
        this.step = 'result';
      },
      error: (error) => {
        this.step = 'confirm';
        this.errorMessage =
          error?.error?.message ||
          error?.message ||
          'Could not switch employees to Employee Controlled. Please try again.';
      },
    });
  }

  onDone(): void {
    this.dialogRef.close(this.successfulResults.length > 0);
  }

  copyCredentials(result: HiddenMigrationResult): void {
    const text = `${result.name}\nEmail: ${result.email}\nPassword: ${result.password}`;
    void navigator.clipboard?.writeText(text);
  }

  copyAllCredentials(): void {
    const text = this.successfulResults
      .map(
        (result) =>
          `${result.name} — ${result.email} — ${result.password}`
      )
      .join('\n');
    void navigator.clipboard?.writeText(text);
  }
}
